<!--
  DpiStatusIcon -- the low-resolution icon on a placement row and a product
  row (card #573, Jake 2026-10-28).

  The severity is the SERVER's call: "warn" (amber: prints soft) or "stop"
  (red: below the method's minimum DPI). Since Jake 2026-10-29 the default is
  to WARN, never block, so a red stop-style icon reads "low resolution, you
  can still print"; a Super Admin may switch a method to refuse instead. Pass the server's verdict as `resolution` (a placement),
  or just `severity` (+ optional `message`) where only a rollup is known (a
  product). The tooltip (and accessible name) is the server's sentence; the
  soft tier, which the server sends no sentence for, is worded from its
  numbers like the notice under the size; a plain fallback covers the rest.
  The testid
  carries the tier (`<testid>-warn` / `<testid>-stop`) so a scenario can name
  it. Renders nothing without a known severity.

  Icons are the app's default Material ligature set (the md font), like the
  invalid-placement icon beside it; "mdi-*" names render as raw text there.
-->
<template>
    <span
        v-if="shown"
        :data-testid="`${testid}-${level}`"
        :data-severity="level"
        class="dpi-status-icon"
        :class="`dpi-status-icon--${level}`"
        role="img"
        tabindex="0"
        :title="text"
        :aria-label="text"
    >
        <span
            class="material-icons dpi-status-icon__glyph"
            aria-hidden="true"
        >{{ level === 'stop' ? 'report' : 'warning' }}</span>
    </span>
</template>

<script setup>
import { computed } from "vue";

const props = defineProps({
    resolution: { type: Object, default: null },
    severity: { type: String, default: null },
    message: { type: String, default: null },
    testid: { type: String, default: "dpi-icon" },
});

const FALLBACK = {
    stop: "Low resolution: artwork on this may print poorly at its size. You can still print it.",
    warn: "Artwork on this is below the recommended resolution at its size. It may print soft.",
};

const level = computed(() => props.severity ?? props.resolution?.severity ?? null);
const shown = computed(() => level.value === "warn" || level.value === "stop");
const text = computed(() => {
    if (props.message) return props.message;
    const r = props.resolution;
    if (r?.message) return r.message;
    if (level.value === "warn" && r?.dpi != null && r?.threshold != null) {
        return `This image is about ${r.dpi} DPI at its size (${r.threshold} recommended). It may print soft.`;
    }
    return FALLBACK[level.value] || "";
});
</script>

<style scoped>
.dpi-status-icon {
    display: inline-flex;
    align-items: center;
    flex: none;
    cursor: help;
}
.dpi-status-icon__glyph {
    font-size: 18px;
    line-height: 1;
}
.dpi-status-icon--warn {
    color: var(--tf-warning, #e0a100);
}
.dpi-status-icon--stop {
    color: var(--tf-danger, #b3261e);
}
</style>
